import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../providers/LocaleProvider';
import {
  contentService,
  ContentBlockType,
  LessonDetailDto,
  LessonExerciseSummaryDto,
  TaggedContentBlockDto
} from '../services/contentService';
import { useNotifications } from './feedback/NotificationSystem';
import { patchLessonExerciseCompleted } from '../utils/patchLessonProgress';
import { ExerciseModal, ExerciseType } from './learning/ExerciseModal';
import { subscriptionService } from '../services/subscriptionService';
import { SubscriptionPlan } from '../types/subscription';
import { ExerciseLockedModal } from './shared/ExerciseLockedModal';
import { LessonJourneyTimeline } from './learning/LessonJourneyTimeline';
import { LessonPracticeQuizExpandables } from './learning/LessonPracticeQuizExpandables';
import { isDesignedForNonFarsiUsers } from '../utils/nonFarsiContentHint';
import { notifyGamificationAfterAction } from '../utils/gamificationNotif';
import {
  buildTagJourneySteps,
  readTagItemViewed,
  tagJourneyGatesClear,
  writeTagItemViewed
} from '../utils/tagJourneySteps';

const TagExerciseOpenContext = createContext<(exerciseId: number) => void>(() => undefined);

export function useOpenTagExercise(): (exerciseId: number) => void {
  return useContext(TagExerciseOpenContext);
}

function taggedExercises(lessons: LessonDetailDto[], blocks: TaggedContentBlockDto[]): LessonExerciseSummaryDto[] {
  const blockIds = new Set(blocks.map((block) => block.id));
  const exerciseIds = new Set<number>();
  for (const block of blocks) {
    if (block.lessonExerciseId != null) exerciseIds.add(block.lessonExerciseId);
  }
  for (const lesson of lessons) {
    for (const block of lesson.contentBlocks ?? []) {
      if (blockIds.has(block.id) && block.lessonExerciseId != null) {
        exerciseIds.add(block.lessonExerciseId);
      }
    }
  }
  const byId = new Map<number, LessonExerciseSummaryDto>();
  for (const lesson of lessons) {
    for (const exercise of lesson.exercises ?? []) {
      if (exerciseIds.has(exercise.id)) byId.set(exercise.id, exercise);
    }
  }
  return [...byId.values()].sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Journey for this tag lesson. Steps are the tags themselves, not the source lesson.
 * Viewing the tags passes the lesson unless a tagged exercise is mandatory.
 */
export const TagBlockSourceLessons: React.FC<{
  tagId: number;
  scopeId: string;
  blocks: TaggedContentBlockDto[];
  passed: boolean;
  onBlocksPassed: (blockIds: number[], passed: boolean) => void;
  children?: React.ReactNode;
}> = ({ tagId, scopeId, blocks, passed, onBlocksPassed, children }) => {
  const { t } = useTranslation(['learning', 'notifications']);
  const { locale } = useAppLocale();
  const { showError, showSuccess } = useNotifications();
  const [lessons, setLessons] = useState<LessonDetailDto[]>([]);
  const [viewed, setViewed] = useState(() => readTagItemViewed(tagId, scopeId, passed));
  const [markingView, setMarkingView] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [userPlanIds, setUserPlanIds] = useState<number[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<{ id: number; type: ExerciseType } | null>(null);
  const [lockedExerciseModal, setLockedExerciseModal] = useState<{
    exerciseTitle: string;
    requiredPlans: { id: number; name: string; description?: string }[];
  } | null>(null);
  const syncedPassRef = useRef<boolean | null>(null);
  const blockIds = useMemo(() => blocks.map((block) => block.id), [blocks]);
  const blockKey = blockIds.join(',');
  const lessonIds = useMemo(
    () => [...new Set(blocks.map((block) => block.lessonId))],
    [blocks]
  );
  const lessonKey = lessonIds.join(',');

  useEffect(() => {
    setViewed(readTagItemViewed(tagId, scopeId, passed));
    syncedPassRef.current = null;
  }, [tagId, scopeId]);

  useEffect(() => {
    if (!lessonKey) {
      setLessons([]);
      return;
    }
    const ids = lessonKey.split(',').map((id) => Number(id)).filter((id) => id > 0);
    let cancelled = false;
    void Promise.all(ids.map((id) => contentService.getLessonDetail(id).catch(() => null))).then((details) => {
      if (cancelled) return;
      setLessons(details.filter((detail): detail is LessonDetailDto => detail != null));
    });
    return () => {
      cancelled = true;
    };
  }, [lessonKey]);

  useEffect(() => {
    void (async () => {
      try {
        const [plans, status] = await Promise.all([
          subscriptionService.getPlans(),
          subscriptionService.getSubscriptionStatus().catch(() => null)
        ]);
        setSubscriptionPlans(plans);
        const planId = status?.currentSubscription?.subscriptionPlan?.id;
        setUserPlanIds(planId != null ? [planId] : []);
      } catch {
        setSubscriptionPlans([]);
        setUserPlanIds([]);
      }
    })();
  }, [tagId]);

  const exercises = useMemo(() => taggedExercises(lessons, blocks), [lessons, blocks]);
  const mandatoryExercises = useMemo(
    () => exercises.filter((exercise) => exercise.mandatoryForPassingLesson === true),
    [exercises]
  );
  const exerciseLessonIds = useMemo(
    () => [...new Set(blocks.filter((block) => block.type === ContentBlockType.Exercise).map((block) => block.lessonId))],
    [blocks]
  );
  const exercisesReady = exerciseLessonIds.every((id) => lessons.some((lesson) => lesson.id === id));
  const shouldPass = viewed && tagJourneyGatesClear(mandatoryExercises, []);

  useEffect(() => {
    if (!exercisesReady || blockIds.length === 0) return;
    if (syncedPassRef.current === shouldPass && shouldPass === passed) return;
    if (shouldPass === passed && syncedPassRef.current === shouldPass) return;
    syncedPassRef.current = shouldPass;
    if (shouldPass === passed) return;
    let cancelled = false;
    void (async () => {
      try {
        for (const id of blockIds) {
          await contentService.setTagBlockProgress(tagId, id, shouldPass);
        }
        if (cancelled) return;
        onBlocksPassed(blockIds, shouldPass);
        if (shouldPass) notifyGamificationAfterAction();
      } catch {
        syncedPassRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exercisesReady, shouldPass, passed, blockKey, tagId, onBlocksPassed, blockIds]);

  const patchExercise = useCallback((exerciseId: number) => {
    setLessons((prev) => prev.map((lesson) => patchLessonExerciseCompleted(lesson, exerciseId)));
  }, []);

  const handleStartExercise = useCallback(async (ex: { id: number; title: string; subscriptionPlanIds?: number[] }) => {
    const requiredPlanIds = ex.subscriptionPlanIds ?? [];
    const hasAccess = requiredPlanIds.length === 0 || requiredPlanIds.some((id) => userPlanIds.includes(id));
    if (!hasAccess) {
      const requiredPlans = requiredPlanIds
        .map((id) => subscriptionPlans.find((plan) => plan.id === id))
        .filter((plan): plan is SubscriptionPlan => !!plan)
        .map((plan) => ({ id: plan.id, name: plan.name, ...(plan.description != null && { description: plan.description }) }));
      setLockedExerciseModal({ exerciseTitle: ex.title, requiredPlans });
      return;
    }
    try {
      await contentService.getLessonExerciseForLearner(ex.id);
      setSelectedExercise({ id: ex.id, type: 'lesson' });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('exercise.loadFailed'));
    }
  }, [showError, subscriptionPlans, t, userPlanIds]);

  const steps = buildTagJourneySteps({
    viewed,
    passed: shouldPass,
    markingView,
    exercises: mandatoryExercises,
    quizzes: [],
    onMarkViewed: () => {
      setMarkingView(true);
      writeTagItemViewed(tagId, scopeId, true);
      setViewed(true);
      setMarkingView(false);
      showSuccess(t('journey.markedViewed', { defaultValue: 'Marked as viewed' }));
    },
    onUnmarkViewed: () => {
      setMarkingView(true);
      writeTagItemViewed(tagId, scopeId, false);
      setViewed(false);
      setMarkingView(false);
      showSuccess(t('journey.unmarkedViewed', { defaultValue: 'Marked as not viewed' }));
    },
    onOpenExercise: (exerciseId) => {
      const exercise = exercises.find((item) => item.id === exerciseId);
      if (!exercise) return;
      void handleStartExercise({
        id: exercise.id,
        title: exercise.title,
        subscriptionPlanIds: exercise.subscriptionPlanIds ?? []
      });
    },
    onOpenQuiz: () => undefined,
    jt: (key, opts) => t(key, { defaultValue: undefined, ...(opts ?? {}) }),
    locale
  });

  const activeExerciseForceEnglish =
    selectedExercise != null &&
    isDesignedForNonFarsiUsers(exercises.find((exercise) => exercise.id === selectedExercise.id));

  return (
    <TagExerciseOpenContext.Provider value={(exerciseId) => {
      const exercise = exercises.find((item) => item.id === exerciseId);
      void handleStartExercise({
        id: exerciseId,
        title: exercise?.title ?? '',
        subscriptionPlanIds: exercise?.subscriptionPlanIds ?? []
      });
    }}>
      {blocks.length > 0 ? (
        <LessonJourneyTimeline steps={steps} persistExpandLessonId={blockIds[0]} />
      ) : null}
      {children}
      {exercises.length > 0 ? (
        <LessonPracticeQuizExpandables
          exercises={exercises}
          quizzes={[]}
          subscriptionPlans={subscriptionPlans}
          onStartExercise={(exercise) => void handleStartExercise(exercise)}
          onStartQuiz={() => undefined}
        />
      ) : null}
      {selectedExercise && (
        <ExerciseModal
          exerciseType={selectedExercise.type}
          exerciseId={selectedExercise.id}
          contentForceEnglish={activeExerciseForceEnglish}
          onClose={() => setSelectedExercise(null)}
          onLessonExerciseCompleted={() => patchExercise(selectedExercise.id)}
        />
      )}
      {lockedExerciseModal && (
        <ExerciseLockedModal
          open
          exerciseTitle={lockedExerciseModal.exerciseTitle}
          requiredPlans={lockedExerciseModal.requiredPlans}
          onClose={() => setLockedExerciseModal(null)}
        />
      )}
    </TagExerciseOpenContext.Provider>
  );
};
